import { useRef, useCallback, useEffect } from 'react';
import { message as antMessage } from 'antd';
import { connectToSSE } from '../../../services/chatService';

interface UseSSEConnectionProps {
    onMessage: (event: MessageEvent) => void;
    onClose: () => void;
    onError: (error: Event) => void;
    setError: (error: string | null) => void;
    setIsLoading: (loading: boolean) => void;
}

export const useSSEConnection = () => {
    const eventSourceRef = useRef<EventSource | null>(null);
    const currentAssistantMessage = useRef('');

    // 清理 EventSource 连接
    const cleanupEventSource = useCallback(() => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
    }, []);

    // 创建新的SSE连接
    const createConnection = useCallback((
        text: string,
        account_id: string,
        thread_id: string,
        {
            onMessage,
            onClose,
            onError,
            setError,
            setIsLoading
        }: UseSSEConnectionProps
    ) => {
        try {
            // 清理现有连接
            cleanupEventSource();

            // 创建新连接
            const newEventSource = connectToSSE(text, account_id, thread_id);
            eventSourceRef.current = newEventSource;

            // 添加事件监听器
            newEventSource.addEventListener('message', onMessage);
            newEventSource.addEventListener('close', () => {
                setIsLoading(false);
                cleanupEventSource();
                onClose();
            });
            newEventSource.addEventListener('error', (event) => {
                setIsLoading(false);
                cleanupEventSource();
                onError(event);
            });

            return true;
        } catch (error) {
            setIsLoading(false);
            const errorMessage = error instanceof Error ? error.message : '连接失败';
            setError(errorMessage);
            antMessage.error(errorMessage);
            console.error('SSE连接创建失败:', error);
            return false;
        }
    }, [cleanupEventSource]);

    // 组件卸载时清理连接
    useEffect(() => {
        return cleanupEventSource;
    }, [cleanupEventSource]);

    return {
        currentAssistantMessage,
        createConnection
    };
}; 